import React, { useState } from 'react';
import { Check, Minus, XCircle, Trash2, MoreVertical, Edit, PlusCircle } from 'lucide-react';
import { Plan } from './types/planTypes';
import { Button } from '@/components/ui/button';
import EditPlanModal from './EditPlanModal';
import AddChargeModal from './AddChargeModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  TableCell,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface PlanRowProps {
  plan: Plan;
  quarterlyVersions: string[];
  isPlanInVersion: (planId: string | null | undefined, version: string) => boolean;
  isScreenshotInvalid: (version: string) => boolean;
  openImageModal: (version: string, planName: string | null) => void;
  isLegacyPlan: (planId: string) => boolean;
  showCurrentOnly: boolean;
  openDeleteDialog?: (plan: Plan) => void;
}

const PlanRow: React.FC<PlanRowProps> = ({
  plan,
  quarterlyVersions,
  isPlanInVersion,
  isScreenshotInvalid,
  openImageModal,
  isLegacyPlan,
  showCurrentOnly,
  openDeleteDialog
}) => {
  // State for modals
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddChargeModalOpen, setIsAddChargeModalOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Get the slug from global variables or route params
  const currentSlug = (window as any).currentSlug || 
                     (window as any).slug || 
                     '';
  
  // CRITICAL: Get a valid page_id that exists in the pages table to satisfy the foreign key constraint
  // This value is used for creating entries in the charges_map table
  const effectivePageId = 
    // First, try to get page_id directly from the plan - this should come from the page_data view
    plan.page_id || 
    // Then try the actual page ID (most likely this is what exists in the pages table)
    ((window as any).activePage?.id) || 
    ((window as any).page?.id) || 
    // Check common global variables that might hold page info
    (window as any).GLOBAL_PAGE_ID || 
    (window as any).pageId || 
    // Get the first company page ID from any available company data
    ((window as any).company?.pages?.[0]?.id) || 
    ((window as any).companyData?.pages?.[0]?.id) || 
    // IMPORTANT: Using plan.id is NOT valid for the foreign key constraint!
    // We should NOT use plan.id as a fallback for page_id
    null;
    
  // Log for debugging where we're getting the page_id from
  console.log('PlanRow effectivePageId:', {
    planId: plan.id,
    planPageId: plan.page_id,
    effectivePageId,
    planDataSummary: {
      name: plan.display_name || plan.plan_name,
      hasPageId: !!plan.page_id
    }
  });
                           
  // For debugging - check global variables that might have version info
  console.log('Global version variables:', {
    windowVersions: (window as any).versions,
    windowQuarterlyVersions: (window as any).quarterlyVersions,
    globalVersions: (window as any).GLOBAL_VERSIONS,
    availableVersions: (window as any).availableVersions
  });
  // Special handling for empty state
  if (plan.id === 'empty-plan') {
    return (
      <TableRow className="bg-gray-50">
        <TableCell className="font-medium text-gray-400">
          <div className="truncate max-w-[180px]">
            {plan.display_name || plan.plan_name}
          </div>
        </TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        <TableCell></TableCell>
        {/* Latest column for empty state */}
        <TableCell className="text-center">
          <div className="flex justify-center">
            <span className="text-gray-300">—</span>
          </div>
        </TableCell>
        {quarterlyVersions.map(version => (
          <TableCell
            key={version}
            className="text-center cursor-pointer hover:bg-blue-50"
            onClick={() => openImageModal(version, "All Plans")}
          >
            <div className="flex justify-center">
              <span className="text-gray-300">—</span>
            </div>
          </TableCell>
        ))}
        <TableCell>
          {/* Only show delete button if openDeleteDialog is provided */}
          {openDeleteDialog && (
            <div className="flex justify-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6 text-gray-400 hover:text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteDialog(plan);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Delete this plan</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow className={!showCurrentOnly && plan.id && isLegacyPlan(plan.id) ? "bg-blue-50" : "bg-gray-50"}>
      <TableCell
        className="font-medium cursor-pointer"
        onClick={() => setIsEditModalOpen(true)}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="text-left">
              <div className="truncate max-w-[180px]">
                {plan.display_name || plan.plan_name}
                {plan.add_on && <span className="ml-2 text-xs text-gray-500">(Add-on)</span>}
                {!showCurrentOnly && plan.id && isLegacyPlan(plan.id) && <span className="ml-2 text-xs text-blue-500">(Legacy)</span>}
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{plan.display_name || plan.plan_name}</p>
              {plan.add_on && <p className="text-xs text-gray-500">Add-on plan</p>}
              {!showCurrentOnly && plan.id && isLegacyPlan(plan.id) && <p className="text-xs text-blue-500">Legacy plan (not in current quarter)</p>}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      
      {/* Empty Pricing Metric column for plan rows */}
      <TableCell></TableCell>
      
      <TableCell
        className="text-sm text-gray-600 cursor-pointer"
        onClick={() => setIsEditModalOpen(true)}
      >
        {plan.add_on && (
          <span className="text-xs font-medium">
            Add-on
          </span>
        )}
      </TableCell>
      <TableCell
        className="cursor-pointer"
        onClick={() => setIsEditModalOpen(true)}
      ></TableCell>
      
      {/* Latest column */}
      <TableCell
        className="text-center cursor-pointer hover:bg-blue-50"
        onClick={() => {
          // Find the latest version by sorting versions
          const latestVersion = [...quarterlyVersions].sort((a, b) => {
            return parseInt(b) - parseInt(a); // Assuming version is a numeric string
          })[0];
          
          // Show screenshot if available
          if (latestVersion) {
            openImageModal(latestVersion, plan.display_name || plan.plan_name + ' (Latest)');
          }
        }}
      >
        <div className="flex justify-center">
          <span className="text-gray-400">
            {(() => {
              // Find latest version
              const latestVersion = [...quarterlyVersions].sort((a, b) => {
                return parseInt(b) - parseInt(a); // Sort in descending order
              })[0];
              
              return isPlanInVersion(plan.id, latestVersion) ? " " : " ";
            })()}
          </span>
        </div>
      </TableCell>
      
      {quarterlyVersions.map(version => (
        <TableCell
          key={version}
          className="text-center cursor-pointer hover:bg-blue-50"
          onClick={() => openImageModal(version, plan.display_name || plan.plan_name)}
        >
          <div className="flex justify-center">
            <span className="text-gray-400">
              {isPlanInVersion(plan.id, version) ? " " : " "}
            </span>
          </div>
        </TableCell>
      ))}
      <TableCell>
        {/* Only show actions menu if openDeleteDialog is provided */}
        {openDeleteDialog && (
          <div className="flex justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-gray-400 hover:text-gray-600"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="focus:bg-blue-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditModalOpen(true);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2 text-blue-600" />
                  <span>Edit Plan</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="focus:bg-green-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsAddChargeModalOpen(true);
                  }}
                >
                  <PlusCircle className="h-4 w-4 mr-2 text-green-600" />
                  <span>Add Charge</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteDialog(plan);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  <span>Delete Plan</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </TableCell>
      {/* Edit Plan Modal */}
      <EditPlanModal
        plan={plan}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={(updatedPlan) => {
          // We don't need to update local state as the parent will re-render with new data
          setIsEditModalOpen(false);
          
          // Use the global refresh function if available
          const refreshCompanyData = (window as any).refreshCompanyData;
          if (typeof refreshCompanyData === 'function') {
            refreshCompanyData();
          }
        }}
      />
      
      {/* Add Charge Modal */}
      <AddChargeModal
        plan={plan}
        isOpen={isAddChargeModalOpen}
        onClose={() => setIsAddChargeModalOpen(false)}
        onSuccess={(newCharge) => {
          // We don't need to update local state as the parent will re-render with new data
          setIsAddChargeModalOpen(false);
          
          // Use the global refresh function if available
          const refreshCompanyData = (window as any).refreshCompanyData;
          if (typeof refreshCompanyData === 'function') {
            refreshCompanyData();
          }
        }}
        // Pass the exact same quarterlyVersions that PlansTable passed to us
        quarterlyVersions={quarterlyVersions}
        slug={currentSlug}
        pageId={effectivePageId}
      />
    </TableRow>
  );
};

export default PlanRow;
